import { useCallback, useEffect, useMemo, useState } from 'react'
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  fetchAssets,
  fetchClientBalances,
  fetchCustomDataset,
  fetchLevelDates,
  getAssetTearSheet,
  getHolidays,
  postNamedQuery,
  getAssetClassificationTagTypes,
  fetchCoreSeriesData,
  postNamedCommand
} from '../service'
import { useAppContext } from '../redux/slices/appContext'
import { mapDatesIntoTree } from '../utils'
import { QUERY_KEYS } from './queryKeys'
import { queryProcessor } from './deferredQueryProcessor'

export const useAssetClassificationTagTypes = () => {
  const { userId } = useAppContext()
  return useQuery({
    queryKey: [QUERY_KEYS.assetClassificationTagTypes, userId],
    queryFn: async () => {
      const { data } = await getAssetClassificationTagTypes()
      return data
    }
  })
}

export const useAssets = (query) => {
  const { userId } = useAppContext()
  return useQuery({
    queryKey: ['assets', userId, query],
    queryFn: async () => {
      const { data } = await fetchAssets(query)

      return data
    }
  })
}

export const useClientBalances = (query) => {
  const { userId } = useAppContext()
  return useQuery({
    queryKey: ['clientBalances', userId, query],
    queryFn: async () => {
      const { data } = await fetchClientBalances(query)

      return data
    }
  })
}

export const useLevelDates = (query) => {
  const { userId } = useAppContext()
  return useQuery({
    queryKey: [QUERY_KEYS.levelDates, userId, query],
    queryFn: async () => {
      const { data } = await fetchLevelDates(query)

      return data
    }
  })
}

export const useClientDates = (clientId) => {
  const { userId } = useAppContext()
  return useQuery({
    queryKey: [QUERY_KEYS.levelDates, 'clientDates', userId, clientId],
    queryFn: async () => {
      const { data } = await fetchLevelDates({
        levelType: 'client',
        clientIds: [clientId]
      })
      return data
    },
    enabled: !!clientId
  })
}

export const useCustodians = (queryOptions = {}) => {
  const { userId } = useAppContext()
  const { mapper, enabled } = queryOptions
  return useQuery({
    queryKey: [QUERY_KEYS.custodians, userId],
    queryFn: async () => {
      const { data } = await postNamedQuery('coreData', 'custodians', {})
      return data
    },
    select: mapper,
    enabled
  })
}

export const useHolidays = (options = {}) => {
  const { enabled = true } = options
  return useQuery({
    enabled,
    queryKey: ['holidays'],
    queryFn: async () => {
      const { data } = await getHolidays({})

      return mapDatesIntoTree(data)
    }
  })
}

export const useCoreDataDataset = (query, options = {}) => {
  const { userId } = useAppContext()
  const { enabled = true } = options
  return useQuery({
    enabled,
    queryKey: [QUERY_KEYS.coreDataDataset, userId, query],
    queryFn: async () => {
      const { data } = await fetchCustomDataset('coreData', query)
      return data
    }
  })
}

export const useClientNetWorth = (query, options = {}) => {
  const { userId } = useAppContext()
  const { enabled = true } = options
  return useQuery({
    enabled,
    queryKey: [QUERY_KEYS.clientNetWorth, userId, query],
    queryFn: async () => {
      return await queryProcessor(async q => await postNamedQuery('coreData', 'get-client-net-worth', q), query)
    }
  })
}

export const useAccountSearch = (query, options = {}) => {
  const { userId } = useAppContext()
  const enabled = options?.enabled ?? true
  return useQuery({
    queryKey: [QUERY_KEYS.accountSearch, userId, query],
    queryFn: async () => {
      const { data } = await postNamedQuery('levels', 'searchAccounts', query)
      return data
    },
    enabled
  })
}

export const useAccountSearchMultiple = (query) => {
  const { userId } = useAppContext()
  return useQuery({
    queryKey: [QUERY_KEYS.accountSearchMultiple, userId, query],
    queryFn: async () => {
      const { data } = await postNamedQuery('levels', 'searchAccountsMultiple', query)
      return data
    }
  })
}

export const useAccountStatusDetailsSearch = (query, multiple = false) => {
  const { userId } = useAppContext()
  const queryName = useMemo(() => multiple ? 'searchAccountsMultiple' : 'searchAccounts', [multiple])
  return useQuery({
    queryKey: [QUERY_KEYS.accountStatusDetailsSearch, userId, query, queryName],
    queryFn: async () => {
      const { data } = await postNamedQuery('levels', queryName, query)
      return data
    },
    keepPreviousData: true
  })
}

export const useTransactionsSearch = (query, options = {}) => {
  const { userId } = useAppContext()
  const { enabled = true } = options
  return useQuery({
    enabled,
    queryKey: [QUERY_KEYS.searchTransactions, userId, query],
    queryFn: async () => {
      if (!query) return null
      return await queryProcessor(async q => await postNamedQuery('coreData', 'transactions', q), query)
    }
  })
}

export const useTransactionCodeTagSearch = (query, options = {}) => {
  const { userId } = useAppContext()
  const { enabled = true, mapper } = options
  return useQuery({
    enabled,
    queryKey: [QUERY_KEYS.searchTransactionCodeTags, userId, query],
    queryFn: async () => {
      const { data } = await postNamedQuery('coreData', 'transactionCodeTags', query)
      return data
    },
    select: mapper
  })
}

export const useAssetSearchDeprecated = (query, options = {}) => {
  const { userId } = useAppContext()
  const { enabled = true, mapper } = options
  return useQuery({
    enabled,
    queryKey: [QUERY_KEYS.searchAssets, userId, query],
    queryFn: async () => {
      const { data } = await postNamedQuery('coreData', 'assets', query)
      return data
    },
    select: mapper
  })
}

export const useAssetTypes = (options = {}) => {
  const { enabled = true, mapper } = options
  return useQuery({
    enabled,
    queryKey: [QUERY_KEYS.assetTypes],
    queryFn: async () => {
      const { data } = await postNamedQuery('coreData', 'get-asset-types')
      return data
    },
    select: mapper
  })
}

export const useAssetExposureSearch = (query, options = {}) => {
  const { userId } = useAppContext()
  const { enabled = true } = options
  return useQuery({
    enabled,
    queryKey: [QUERY_KEYS.searchAssetExposure, userId, query],
    queryFn: async () => {
      return await queryProcessor(async q => postNamedQuery('coreData', 'assetExposure', q), query)
    }
  })
}

export const useRunBookOfBusinessReport = (query, options = {}) => {
  const { userId } = useAppContext()
  const { enabled = true } = options
  return useQuery({
    enabled,
    queryKey: ['runBobbi', userId, query],
    queryFn: async () => {
      const { data } = await postNamedQuery('coreData', 'runAssetExposure', query)
      return data
    }
  })
}

export const useQueryBookOfBusinessReport = (query, options = {}) => {
  const { userId } = useAppContext()
  const { enabled = true } = options
  return useQuery({
    enabled,
    queryKey: ['queryBobbi', userId, query],
    refetchInterval: (data) => {
      if (!data) return options?.interval || 5000

      return undefined
    },
    queryFn: async () => {
      const { data } = await postNamedQuery('coreData', 'queryAssetExposure', query)
      return data
    }
  })
}

export const useGainLossSearch = (query, options = {}) => {
  const { userId } = useAppContext()
  const { enabled = true } = options
  const result = useQuery({
    enabled,
    queryKey: [QUERY_KEYS.searchGainLoss, userId, query],
    queryFn: async () => {
      return await queryProcessor(async q => await postNamedQuery('coreData', 'gainLoss', q), query)
    }
  })

  const queryClient = useQueryClient()
  const fetchMore = useCallback(async query => {
    return await queryClient.fetchQuery({
      queryKey: [QUERY_KEYS.searchGainLoss, userId, query],
      queryFn: async () => {
        return await queryProcessor(async q => await postNamedQuery('coreData', 'gainLoss', q), query)
      }
    })
  }, [queryClient, userId])
  return {
    ...result,
    fetchMore
  }
}

export const useComponentsOfChangeQuery = (query, options = {}) => {
  const { userId } = useAppContext()
  const { enabled = true, keepPreviousData, mapper } = options
  return useQuery({
    enabled,
    keepPreviousData,
    queryKey: [QUERY_KEYS.componentsOfChangeQuery, userId, query],
    queryFn: async () => {
      return await queryProcessor(async q => await postNamedQuery('coreData', 'componentsOfChange', q), query)
    },
    select: mapper
  })
}

export const useClassificationTags = (query, options = {}) => {
  const { userId } = useAppContext()
  const { enabled = true, mapper } = options
  return useQuery({
    enabled,
    queryKey: [QUERY_KEYS.classificationTags, userId, query],
    queryFn: async () => {
      const { data } = await postNamedQuery(
        'coreData',
        'classificationTags',
        query
      )
      return data
    },
    select: mapper
  })
}

export const useClassificationTagTypes = (query, options = {}) => {
  const { userId } = useAppContext()
  const { enabled = true, mapper } = options
  const queryKey = query ? JSON.stringify(query) : null
  return useQuery({
    enabled,
    queryKey: [QUERY_KEYS.classificationTagTypes, userId, queryKey],
    queryFn: async () => {
      const { data } = await postNamedQuery(
        'coreData',
        'classificationTagTypes',
        query
      )
      return data
    },
    select: mapper
  })
}

export const useAssetsHoldings = (query, options) => {
  const { userId } = useAppContext()
  const { enabled = true } = options
  return useQuery({
    queryKey: [QUERY_KEYS.assetHoldings, userId, query],
    queryFn: async () => {
      const { data } = await postNamedQuery(
        'coreData',
        'positions',
        query
      )
      return data
    },
    enabled
  })
}

function getCoreTableData (query) {
  return async () => {
    return await queryProcessor(async q => await postNamedQuery('coreData', 'getCoreTableData', q), query)
  }
}

function getCoreTable (query) {
  return async () => {
    return await queryProcessor(async q => await postNamedQuery('coreData', 'getGroupedCoreData', q), query)
  }
}

export const useGetGroupedCoreData = (query, options = {}) => {
  const { userId } = useAppContext()
  const { enabled = true, keepPreviousData, mapper, queryKey } = options
  return useQuery({
    enabled,
    keepPreviousData,
    queryKey: [queryKey || QUERY_KEYS.getGroupedCoreData, userId, query],
    queryFn: getCoreTable(query),
    select: mapper
  })
}

export const useCoreTableData = (query, options = {}) => {
  const { userId } = useAppContext()
  const { enabled = true, keepPreviousData } = options
  return useQuery({
    enabled,
    keepPreviousData,
    queryKey: [QUERY_KEYS.getCoreTableDataQuery, userId, query],
    queryFn: getCoreTableData(query)
  })
}

export const useCoreTableDataMultiple = (queries, options = {}) => {
  const { userId } = useAppContext()
  const { enabled = true } = options
  const queryMap = useMemo(() => {
    return queries.map((query) => ({
      enabled,
      queryKey: [QUERY_KEYS.getCoreTableDataQuery, userId, query],
      queryFn: getCoreTableData(query)
    }))
  }, [queries, userId, enabled])

  const results = useQueries({
    queries: queryMap
  })

  /** React Query returns a new array every render, this helps determine us memoize the result array */
  const maxResultVersion = useMemo(() => {
    return results.reduce((prev, cur) => Math.max(prev, cur.dataUpdatedAt), 0)
  }, [results])

  const isLoading = useMemo(() => {
    return results.reduce((prev, cur) => prev || cur.isLoading || cur.isFetching, false)
  }, [results])

  const [safeResult, setSafeResult] = useState(results)
  useEffect(() => {
    setSafeResult(results)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setSafeResult, maxResultVersion])

  const queryClient = useQueryClient()
  const fetchMore = useCallback(async query => {
    return await queryClient.fetchQuery({
      queryKey: [QUERY_KEYS.getCoreTableDataQuery, userId, query],
      queryFn: getCoreTableData(query)
    })
  }, [queryClient, userId])

  return {
    results: safeResult,
    fetchMore,
    isLoading
  }
}

export const useNormalizeRelativeDates = (query, options = {}) => {
  const { userId } = useAppContext()
  const { enabled = true, mapper } = options
  return useQuery({
    enabled,
    queryKey: [QUERY_KEYS.normalizeRelativeDates, userId, query],
    queryFn: async () => {
      return await queryProcessor(async q => await postNamedQuery('coreData', 'normalizeRelativeDates', q), query)
    },
    select: mapper
  })
}

export const useNormalizeDates = (query, options = {}) => {
  const { userId } = useAppContext()
  const { enabled = true, mapper } = options
  return useQuery({
    enabled,
    queryKey: [QUERY_KEYS.normalizeDates, userId, query],
    queryFn: async () => {
      return await queryProcessor(async q => await postNamedQuery('coreData', 'normalizeDates', q), query)
    },
    select: mapper
  })
}

export const useAlternativesMultiple = (queries, options = {}) => {
  const { userId } = useAppContext()
  const { enabled = true, mapper } = options
  const queryMap = useMemo(() => {
    return queries.map((query) => ({
      enabled,
      queryKey: [QUERY_KEYS.alternatives, userId, query],
      queryFn: async () => {
        return await queryProcessor(async q => await postNamedQuery('coreData', 'alternatives', q), query)
      },
      select: mapper
    }))
  }, [enabled, queries, mapper, userId])

  const results = useQueries({
    queries: queryMap
  })

  /** React Query returns a new array every render, this helps determine us memoize the result array */
  const maxResultVersion = useMemo(() => {
    return results.reduce((prev, cur) => Math.max(prev, cur.dataUpdatedAt), 0)
  }, [results])

  const [safeResult, setSafeResult] = useState(results)
  useEffect(() => {
    setSafeResult(results)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setSafeResult, maxResultVersion])

  const queryClient = useQueryClient()
  const fetchMore = useCallback(async query => {
    return await queryClient.fetchQuery({
      queryKey: [QUERY_KEYS.alternatives, userId, query],
      queryFn: async () => {
        return await queryProcessor(async q => await postNamedQuery('coreData', 'alternatives', q), query)
      }
    })
  }, [queryClient, userId])

  return {
    results: safeResult,
    fetchMore
  }
}

export const useAlternatives = (query) => {
  const { userId } = useAppContext()
  return useQuery({
    queryKey: [QUERY_KEYS.alternatives, userId, query],
    queryFn: async () => {
      return await queryProcessor(async q => await postNamedQuery('coreData', 'alternatives', q), query)
    }
  })
}

export const useAssetTearSheet = (assetId) => {
  const { userId } = useAppContext()
  return useQuery({
    queryKey: [QUERY_KEYS.assetTearSheet, userId, assetId],
    queryFn: async () => {
      const { data } = await getAssetTearSheet(assetId)
      return data
    }
  })
}

export const useProjectedIncome = (query, queryOptions = {}) => {
  const { userId } = useAppContext()
  const { mapper, enabled = true } = queryOptions
  return useQuery({
    queryKey: [QUERY_KEYS.projectedIncome, userId, query],
    queryFn: async () => {
      return await queryProcessor(async q => await postNamedQuery('coreData', 'projectedIncome', q), query)
    },
    select: mapper,
    enabled
  })
}

export const useCoreSeriesData = (query, queryOptions = {}) => {
  const { userId } = useAppContext()
  const { mapper, enabled = true } = queryOptions
  return useQuery({
    queryKey: [QUERY_KEYS.coreSeriesData, userId, query],
    queryFn: async () => {
      return await queryProcessor(async q => await fetchCoreSeriesData(q), query)
    },
    select: mapper,
    enabled
  })
}

export const useBenchmarkReturns = (query, queryOptions = {}) => {
  const { userId } = useAppContext()
  const { mapper, enabled = true } = queryOptions
  return useQuery({
    queryKey: [QUERY_KEYS.getBenchmarkReturns, userId, query],
    queryFn: async () => {
      return await queryProcessor(async q => await postNamedQuery('coreData', 'getBenchmarkReturns', q), query)
    },
    select: mapper,
    enabled
  })
}

export const useFirmReportMetrics = (query, options = {}) => {
  const { userId } = useAppContext()
  const { enabled = true, keepPreviousData = false } = options
  return useQuery({
    queryKey: ['firm-report-metrics', userId, query],
    queryFn: async () => {
      const { data } = await postNamedQuery('coreData', 'get-firm-wide-report-metrics', query)

      return data
    },
    enabled,
    keepPreviousData
  })
}

export const useFirmReportConfiguration = (reportId, options = {}) => {
  const { userId } = useAppContext()
  const { enabled = true } = options
  return useQuery({
    queryKey: ['firm-report-configuration', userId, reportId],
    queryFn: async () => {
      const { data } = await postNamedQuery('coreData', 'get-firm-wide-report-configuration', {
        reportId
      })

      return data
    },
    enabled
  })
}

export const useFirmReportSummary = (reportId, options = {}) => {
  const { userId } = useAppContext()
  const { enabled = true, skip, take, membershipFilter, filters } = options
  const membershipFilterKey = membershipFilter ? JSON.stringify(membershipFilter) : 'null'
  const filterKey = filters ? JSON.stringify(filters) : 'null'
  return useQuery({
    queryKey: ['firm-report-summary', userId, reportId, skip, take, membershipFilterKey, filterKey],
    queryFn: async () => {
      const { data } = await postNamedQuery('coreData', 'get-firm-wide-report-summary', {
        reportId,
        membershipFilter,
        filters,
        skip,
        take
      })

      return data
    },
    enabled
  })
}

export const usePrefetchFirmReportMetrics = () => {
  const { userId } = useAppContext()
  const queryClient = useQueryClient()
  return useCallback(async reportId => {
    await queryClient.prefetchQuery(['firm-report-metrics', userId, reportId], {
      queryFn: async () => {
        const { data } = await postNamedQuery('coreData', 'get-firm-wide-report-metrics', {
          reportId
        })

        return data
      }
    })
  }, [userId, queryClient])
}

export const useFirmReportData = (query, options = {}) => {
  const { userId } = useAppContext()
  const { enabled = true, keepPreviousData = false } = options
  return useQuery({
    queryKey: ['firm-report-data', userId, query],
    queryFn: async () => {
      const { data } = await postNamedQuery('coreData', 'get-firm-wide-report-data', query)

      return data
    },
    enabled,
    keepPreviousData
  })
}

export const useDenaliData = (query, options = {}) => {
  const { userId } = useAppContext()
  const { enabled = true, keepPreviousData = false } = options
  return useQuery({
    queryKey: ['get-denali-data', userId, query],
    queryFn: async () => {
      const { data } = await postNamedQuery('coreData', 'get-denali-data', query)

      return data
    },
    enabled,
    keepPreviousData
  })
}

export const useListFirmReportStatus = (query, options = {}) => {
  const { userId } = useAppContext()
  const { enabled = true } = options
  return useQuery({
    queryKey: ['list-firm-report-status', userId, query],
    queryFn: async () => {
      const { data } = await postNamedQuery('coreData', 'list-firm-wide-report-status', query)

      return data
    },
    enabled
  })
}

export const useListBppReportTypes = (options = {}) => {
  const { userId } = useAppContext()
  const { enabled = true } = options
  return useQuery({
    queryKey: ['list-bpp-report-types', userId],
    queryFn: async () => {
      const { data } = await postNamedQuery('bobi', 'list-bpp-report-types', {})

      return data
    },
    enabled
  })
}

/**
 * lists bpp reports
 * @param {string} reportType
 * @param options
 * @return {{data:{reports:[{reportType: string,bppReportId: number,asOfDate: string,isProcessing: boolean,lastStartedDate: string,lastFinishedDate: string}]}}}
 */
export const useListBppReports = (reportType = undefined, options = {}) => {
  const { userId } = useAppContext()
  const { enabled = true } = options
  return useQuery({
    queryKey: ['list-bpp-reports', userId, reportType],
    queryFn: async () => {
      const { data } = await postNamedQuery('bobi', 'list-bpp-reports', {
        reportType
      })

      return data
    },
    enabled
  })
}

export const useBppReportStatus = (bppReportId, options = {}) => {
  // const queryClient = useQueryClient()
  const { userId } = useAppContext()
  const { enabled = true, refetchInterval = null } = options
  return useQuery({
    queryKey: ['get-bpp-report-status', userId, bppReportId],
    queryFn: async () => {
      const { data } = await postNamedQuery('bobi', 'get-bpp-report-status', {
        bppReportId
      })

      return data
    },
    refetchInterval,
    refetchOnWindowFocus: !!refetchInterval,
    enabled
  })
}

export const useStartBppMutation = () => {
  const queryClient = useQueryClient()
  const { userId } = useAppContext()
  return useMutation({
    mutationFn: async (command) => {
      const { data } = await postNamedCommand('bobi', 'start-bpp', command)

      return data
    },
    onSuccess: (data, variables) => {
      const { reportType } = variables
      queryClient.invalidateQueries({ queryKey: ['list-bpp-reports', userId, reportType], exact: true }).catch(console.error)
    }
  })
}

export const useExpireBppReportMutation = (reportType) => {
  const queryClient = useQueryClient()
  const { userId } = useAppContext()
  return useMutation({
    mutationFn: async (command) => {
      const { data } = await postNamedCommand('bobi', 'expire-bpp-report', command)

      return data
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['list-bpp-reports', userId, reportType], exact: true }).catch(console.error)
    }
  })
}

export const useRestartBppReportMutation = (reportType) => {
  const queryClient = useQueryClient()
  const { userId } = useAppContext()
  return useMutation({
    mutationFn: async (command) => {
      const { data } = await postNamedCommand('bobi', 'restart-bpp-report', command)

      return data
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['list-bpp-reports', userId, reportType], exact: true }).catch(console.error)
    }
  })
}

export const useProjectedIncomeV2 = (query, options = {}) => {
  const { userId } = useAppContext()
  const { enabled = true, mapper } = options
  return useQuery({
    queryKey: ['get-projected-income-v2', userId, query],
    queryFn: async () => {
      return await queryProcessor(async q => await postNamedQuery('coreData', 'projected-income-v2', q), query)
    },
    enabled,
    select: mapper
  })
}

export const useGetTransactionRules = (ruleIds, options = {}) => {
  const { enabled = true, mapper } = options
  return useQuery({
    queryKey: ['get-transaction-rules', JSON.stringify(ruleIds || [])],
    queryFn: async () => {
      const { data } = await postNamedQuery('coreData', 'get-transaction-rules', {
        ruleIds
      })

      const result = data.rules.reduce((prev, cur) => {
        prev[cur.transactionRuleId] = cur.ruleJson
        return prev
      }, {})

      console.log(result)
      return result
    },
    enabled,
    select: mapper
  })
}

export const useComponentsOfChangeV3 = (query, options = {}) => {
  const { enabled = true, mapper } = options
  return useQuery({
    queryKey: ['components-of-change-v3', JSON.stringify(query)],
    queryFn: async () => {
      return await queryProcessor(async q => await postNamedQuery('coreData', 'components-of-change-v3', q), query)
    },
    enabled,
    select: mapper
  })
}

export const useCoreChartData = (query, options = {}) => {
  const { enabled = true, mapper } = options
  return useQuery({
    queryKey: ['get-chart-data', JSON.stringify(query)],
    queryFn: async () => {
      return await queryProcessor(async q => await postNamedQuery('coreData', 'get-chart-data', q), query)
    },
    enabled,
    select: mapper
  })
}

export const useComponentsOfChangeV3Multiple = (queries, options = {}) => {
  const { enabled = true, mapper } = options
  return useQuery({
    queryKey: ['components-of-change-v3-multiple', JSON.stringify(queries)],
    queryFn: async () => {
      const requests = Object.entries(queries).map(async ([key, query]) => {
        const data = await queryProcessor(async q => await postNamedQuery('coreData', 'components-of-change-v3', q), query)

        return {
          key,
          data: data?.data || []
        }
      })
      const result = await Promise.all(requests)
      return result.reduce((prev, cur) => {
        prev[cur.key] = cur.data
        return prev
      }, {})
    },
    enabled,
    select: mapper
  })
}

export const useGetGoalsByCode = (codes = [], options = {}) => {
  const { enabled = true } = options
  return useQuery({
    queryKey: ['coreData/get-goals-by-code', JSON.stringify(codes)],
    queryFn: async () => {
      const { data } = await postNamedQuery('coreData', 'get-goals-by-code', { codes })

      return data
    },
    enabled
  })
}

export const dangerZone = {
  __useGetDenaliReportConfiguration: (reportId) => {
    return useQuery({
      queryKey: ['core/__get-denali-report-configuration', reportId],
      queryFn: async () => {
        const result = await postNamedQuery('coreData', '__get-denali-report-configuration', {
          reportId
        })

        return result.data
      }
    })
  },
  __useGetDenaliReportTableInfo: (reportId) => {
    return useQuery({
      queryKey: ['core/__get-denali-report-table-info', reportId],
      queryFn: async () => {
        const result = await postNamedQuery('coreData', '__get-denali-report-table-info', {
          reportId
        })

        return result.data
      }
    })
  },
  __useGetDenaliReportExportInfo: (reportId) => {
    return useQuery({
      queryKey: ['core/__get-denali-report-export-info', reportId],
      queryFn: async () => {
        const result = await postNamedQuery('coreData', '__get-denali-report-export-info', {
          reportId
        })

        return result.data
      }
    })
  },
  __useModifyDenaliReportColumns: () => {
    const queryClient = useQueryClient()
    return useMutation({
      mutationFn: async (command) => {
        const { data } = await postNamedCommand('coreData', 'modify-denali-report-columns', command)

        return data
      },
      onSuccess: (data, variables) => {
        const { reportId } = variables
        queryClient.invalidateQueries({ queryKey: ['core/__get-denali-report-configuration', reportId], exact: true }).catch(console.error)
      }
    })
  },
  __useModifyDenaliReportAttributes: () => {
    const queryClient = useQueryClient()
    return useMutation({
      mutationFn: async (command) => {
        const { data } = await postNamedCommand('coreData', 'modify-denali-report-attributes', command)

        return data
      },
      onSuccess: (data, variables) => {
        const { reportId } = variables
        queryClient.invalidateQueries({ queryKey: ['core/__get-denali-report-configuration', reportId], exact: true }).catch(console.error)
      }
    })
  },
  __useCreateDenaliReportExportConfiguration: () => {
    const queryClient = useQueryClient()
    return useMutation({
      mutationFn: async (command) => {
        const { data } = await postNamedCommand('coreData', 'create-denali-report-export-configuration', command)

        return data
      },
      onSuccess: (data, variables) => {
        const { reportId } = variables
        queryClient.invalidateQueries({ queryKey: ['core/__get-denali-report-configuration', reportId], exact: true }).catch(console.error)
        queryClient.invalidateQueries({ queryKey: ['core/__get-denali-report-export-info', reportId], exact: true }).catch(console.error)
      }
    })
  },
  __useRemoveDenaliReportExportConfiguration: () => {
    const queryClient = useQueryClient()
    return useMutation({
      mutationFn: async (command) => {
        const { data } = await postNamedCommand('coreData', 'remove-denali-report-export-configuration', command)

        return data
      },
      onSuccess: (data, variables) => {
        const { reportId } = variables
        queryClient.invalidateQueries({ queryKey: ['core/__get-denali-report-configuration', reportId], exact: true }).catch(console.error)
        queryClient.invalidateQueries({ queryKey: ['core/__get-denali-report-export-info', reportId], exact: true }).catch(console.error)
      }
    })
  },
  __useAddDenaliReportFilter: () => {
    const queryClient = useQueryClient()
    return useMutation({
      mutationFn: async (command) => {
        const { data } = await postNamedCommand('coreData', 'add-denali-report-filter', command)

        return data
      },
      onSuccess: (data, variables) => {
        const { reportId } = variables
        queryClient.invalidateQueries({ queryKey: ['core/__get-denali-report-configuration', reportId], exact: true }).catch(console.error)
      }
    })
  },
  __useModifyDenaliReportFilter: () => {
    const queryClient = useQueryClient()
    return useMutation({
      mutationFn: async (command) => {
        const { data } = await postNamedCommand('coreData', 'modify-denali-report-filter', command)

        return data
      },
      onSuccess: (data, variables) => {
        const { reportId } = variables
        queryClient.invalidateQueries({ queryKey: ['core/__get-denali-report-configuration', reportId], exact: true }).catch(console.error)
      }
    })
  },
  __useRemoveDenaliReportFilter: () => {
    const queryClient = useQueryClient()
    return useMutation({
      mutationFn: async (command) => {
        const { data } = await postNamedCommand('coreData', 'remove-denali-report-filter', command)

        return data
      },
      onSuccess: (data, variables) => {
        const { reportId } = variables
        queryClient.invalidateQueries({ queryKey: ['core/__get-denali-report-configuration', reportId], exact: true }).catch(console.error)
      }
    })
  }
}

export const dataPipelines = {
  useDataPipeline: (pipelineId, options = {}) => {
    const enabled = options?.enabled ?? true
    return useQuery({
      queryKey: ['core/get-data-pipeline', pipelineId],
      enabled,
      queryFn: async () => {
        const result = await postNamedQuery('coreData', 'get-data-pipeline', {
          pipelineId
        })

        return result.data
      }
    })
  },
  useExecuteDataPipeline: (request, options = {}) => {
    const enabled = options?.enabled ?? true
    const requestKey = JSON.stringify(request)

    // bypass deferred querying
    const bypass = options?.bypass ?? false
    return useQuery({
      queryKey: ['core/execute-data-pipeline', requestKey, bypass],
      enabled,
      queryFn: async () => {
        if (bypass) {
          const { data } = await postNamedQuery('coreData', 'execute-data-pipeline', request)
          return data
        }
        return await queryProcessor(async q => await postNamedQuery('coreData', 'execute-data-pipeline', q), request)
      }
    })
  },
  useListDataPipelines: () => {
    return useQuery({
      queryKey: ['core/list-data-pipelines'],
      queryFn: async () => {
        const result = await postNamedQuery('coreData', 'list-data-pipelines', {
        })

        return result.data
      }
    })
  },
  useCreateDataPipeline: () => {
    const queryClient = useQueryClient()
    return useMutation({
      mutationFn: async (command) => {
        const { data } = await postNamedCommand('coreData', 'create-data-pipeline', command)

        return data
      },
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries({ queryKey: ['core/list-data-pipelines'], refetchType: 'all' }).catch(console.error)
      }
    })
  },
  useModifyDataPipeline: () => {
    const queryClient = useQueryClient()
    return useMutation({
      mutationFn: async (command) => {
        const { data } = await postNamedCommand('coreData', 'modify-data-pipeline', command)

        return data
      },
      onSuccess: (data, variables) => {
        const { pipelineId } = variables
        queryClient.invalidateQueries({ queryKey: ['core/list-data-pipelines'], refetchType: 'all' }).catch(console.error)
        queryClient.invalidateQueries({ queryKey: ['core/get-data-pipeline', pipelineId], exact: true }).catch(console.error)
      }
    })
  },
  useAddDataPipelineToDataCatalog: () => {
    return useMutation({
      mutationFn: async (command) => {
        const { data } = await postNamedCommand('coreData', 'add-data-pipeline-to-data-catalog', command)

        return data
      }
    })
  }
}

export const dataCatalog = {
  useListCatalogDataSources: (options = {}) => {
    const enabled = options?.enabled ?? true
    return useQuery({
      queryKey: ['core/list-catalog-data-sources'],
      enabled,
      queryFn: async () => {
        const result = await postNamedQuery('coreData', 'list-catalog-data-sources', {
        })

        return result.data
      }
    })
  },
  useGetCatalogDataSource: (datasourceId, options = {}) => {
    const enabled = options?.enabled ?? true
    return useQuery({
      queryKey: ['core/get-catalog-data-source', datasourceId],
      enabled,
      queryFn: async () => {
        const result = await postNamedQuery('coreData', 'get-catalog-data-source', {
          datasourceId
        })

        return result.data
      }
    })
  },
  useGetCatalogEventLogs: (query) => {
    const queryKey = query ? JSON.stringify(query) : null
    return useQuery({
      queryKey: ['core/get-catalog-event-logs', queryKey],
      queryFn: async () => {
        const result = await postNamedQuery('coreData', 'get-catalog-event-logs', query)

        return result.data
      }
    })
  },
  useCreateDataPipeline: () => {
    const queryClient = useQueryClient()
    return useMutation({
      mutationFn: async (command) => {
        const { data } = await postNamedCommand('coreData', 'create-data-pipeline', command)

        return data
      },
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries({ queryKey: ['core/list-data-pipelines'], refetchType: 'all' }).catch(console.error)
      }
    })
  },

  useListCatalogSchema: (options = {}) => {
    const { userId } = useAppContext()
    const { includeUnpublished = false, enabled = true } = options

    return useQuery({
      queryKey: ['denali-list-catalog-schema', userId, includeUnpublished],
      queryFn: async () => {
        const { data } = await postNamedQuery('coreData', 'denali-list-catalog-schema', {
          includeUnpublished
        })
        return data
      },
      enabled,
      staleTime: 5 * 60 * 1000 // 5 minutes - schema doesn't change often
    })
  },

  useListCatalogRestrictionTypes: (options = {}) => {
    const { enabled = true } = options

    return useQuery({
      queryKey: ['core/list-catalog-restriction-types'],
      queryFn: async () => {
        const { data } = await postNamedQuery('coreData', 'list-catalog-restriction-types', {
        })
        return data
      },
      enabled,
      staleTime: 20 * 60 * 1000 // 20 minutes - schema doesn't change often
    })
  },

  useListCatalogSchemaItems: (options = {}) => {
    const { enabled = true } = options

    return useQuery({
      queryKey: ['core/list-catalog-schemas'],
      queryFn: async () => {
        const { data } = await postNamedQuery('coreData', 'list-catalog-schemas', {
        })
        return data
      },
      enabled
    })
  },

  useQueryCatalogViewImperative: () => {
    return useMutation({
      mutationFn: async (command) => {
        const { data } = await postNamedQuery('coreData', 'denali-query-catalog-view', command)

        return data
      }
    })
  },

  useSearchCatalogDataSources: (search = {}, options = {}) => {
    const searchKey = JSON.stringify(search || {})
    const enabled = options?.enabled ?? true
    return useQuery({
      queryKey: ['core/search-catalog-data-sources', searchKey],
      queryFn: async () => {
        const { data } = await postNamedQuery('coreData', 'search-catalog-data-sources', search)

        return data
      },
      enabled
    })
  },

  useInternalMapTableToCatalog: () => {
    const queryClient = useQueryClient()
    return useMutation({
      mutationFn: async (command) => {
        const { data } = await postNamedCommand('coreData', '__internal-catalog-map-table', command)

        return data
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['core/search-catalog-data-sources'], refetchType: 'all' }).catch(console.error)
      }
    })
  },

  useModifyDatasourceDatapoints: () => {
    const queryClient = useQueryClient()
    return useMutation({
      mutationFn: async (command) => {
        const { data } = await postNamedCommand('coreData', 'modify-catalog-datasource-datapoints', command)

        return data
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['core/get-catalog-data-source'], refetchType: 'all' }).catch(console.error)
      }
    })
  },

  useModifyDatasourceRestrictions: () => {
    const queryClient = useQueryClient()
    return useMutation({
      mutationFn: async (command) => {
        const { data } = await postNamedCommand('coreData', 'modify-catalog-datasource-restrictions', command)

        return data
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['core/get-catalog-data-source'], refetchType: 'all' }).catch(console.error)
      }
    })
  },

  useModifyDatasourceDescription: () => {
    const queryClient = useQueryClient()
    return useMutation({
      mutationFn: async (command) => {
        const { data } = await postNamedCommand('coreData', 'modify-catalog-datasource-description', command)

        return data
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['core/get-catalog-data-source'], refetchType: 'all' }).catch(console.error)
      }
    })
  },

  useChangeDatasourceSchema: () => {
    const queryClient = useQueryClient()
    return useMutation({
      mutationFn: async (command) => {
        const { data } = await postNamedCommand('coreData', 'change-catalog-datasource-schema', command)

        return data
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['core/get-catalog-data-source'], refetchType: 'all' }).catch(console.error)
      }
    })
  },

  useChangeDatasourceCode: () => {
    const queryClient = useQueryClient()
    return useMutation({
      mutationFn: async (command) => {
        const { data } = await postNamedCommand('coreData', 'change-catalog-datasource-code', command)

        return data
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['core/get-catalog-data-source'], refetchType: 'all' }).catch(console.error)
      }
    })
  },

  useModifyDatapointDescription: () => {
    const queryClient = useQueryClient()
    return useMutation({
      mutationFn: async (command) => {
        const { data } = await postNamedCommand('coreData', 'modify-catalog-datapoint-description', command)

        return data
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['core/get-catalog-data-source'], refetchType: 'all' }).catch(console.error)
      }
    })
  }
}
